import { Alert, Button } from '@components'
import { InvoiceDeliveryMethod } from '@constants'
import { useBusinessContext } from '@context'
import { IInvoice, sendInvoice, SendInvoiceProps } from '@query'
import { extractGlobalApiErrors, setAPIErrors, stripGlobalApiErrorKeys } from '@utils'
import React, { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaPaperPlane } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import SendInvoiceFormBase from './SendInvoiceFormBase'

interface Props {
  invoice: IInvoice
  onSubmit: () => void
  onCancel: () => void
}

const getSendInvoiceToastError = (error: unknown, fallbackMessage: string): string => {
  const { status, data } =
    (error as { status?: number; data?: { message?: unknown } } | undefined) ?? {}

  if (status === 400 && data && typeof data.message === 'string' && data.message.trim()) {
    return data.message
  }

  return fallbackMessage
}

export const SendInvoiceForm: React.FC<Props> = ({ invoice, onSubmit: _onSubmit, onCancel }) => {
  const { slug, data: business } = useBusinessContext()
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const [isDisabled, setIsDisabled] = useState<boolean>()
  const [globalErrors, setGlobalErrors] = useState<string[]>([])

  const invoicingLanguage = invoice.receiver_info?.invoicing_language
  const subjectOptions = {
    businessName: business?.name,
    invoiceNumber: invoice.invoice_number,
    ...(invoicingLanguage ? { lng: invoicingLanguage } : {})
  }
  const defaultEmailSubject =
    business?.invoicing_default_email_subject?.trim() ||
    (invoice.is_credit_note_for
      ? t('invoicing.send.defaultEmailSubjectCreditNote', subjectOptions)
      : t('invoicing.send.defaultEmailSubject', subjectOptions))

  const changeDisabled = (value: boolean) => {
    setIsDisabled(value)
  }

  const methods = useForm<SendInvoiceProps>({
    defaultValues: {
      delivery_method: invoice.delivery_method,
      data: {
        email_subject: defaultEmailSubject,
        email_content: business?.invoicing_default_email_content || ''
      }
    }
  })

  const deliveryMethod = useWatch({
    control: methods.control,
    name: 'delivery_method'
  })
  const isSelfPrint = deliveryMethod === InvoiceDeliveryMethod.SELF_PRINT

  const sendMutation = useMutation<unknown, unknown, SendInvoiceProps>({
    mutationFn: data => sendInvoice({ slug, invoiceId: invoice.id }, data),
    ...{
      onError: error => {
        const { status, data } = (error as any) ?? {}
        if (status === 400 && data && typeof data === 'object') {
          setGlobalErrors(extractGlobalApiErrors(data))
          setAPIErrors(stripGlobalApiErrorKeys(data as Record<string, unknown>), methods.setError)
        }
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'invoices'] })
        _onSubmit()
      }
    }
  })

  const onSubmit = methods.handleSubmit(async data => {
    setGlobalErrors([])
    const promise = sendMutation.mutateAsync(data)
    const markingDelivered = data.delivery_method === InvoiceDeliveryMethod.SELF_PRINT
    toast.promise(promise, {
      loading: markingDelivered
        ? t('invoicing.send.selfPrintSaving')
        : t('invoicing.send.sendLoading'),
      success:
        data.delivery_method === InvoiceDeliveryMethod.SELF_PRINT
          ? t('invoicing.send.selfPrintSendSuccess')
          : t('invoicing.send.sendSuccess'),
      error: error => getSendInvoiceToastError(error, t('invoicing.send.sendError'))
    })
    await promise
  })

  return (
    <FormProvider {...methods}>
      <FormWrapper>
        <form id="send-invoice-form" onSubmit={onSubmit}>
          <SendInvoiceFormBase
            emailContentName="data.email_content"
            emailSubjectName="data.email_subject"
            invoiceId={invoice.id}
            invoiceReceiver={invoice.receiver}
            changeDisabled={changeDisabled}
          />
        </form>

        {globalErrors.length > 0 && (
          <Alert
            type="error"
            title={t('invoicing.send.validationErrorTitle')}
            description={
              <ErrorList>
                {globalErrors.map((message, index) => (
                  <li key={`${index}-${message}`}>{message}</li>
                ))}
              </ErrorList>
            }
            isVisible
            marginBottom={0}
          />
        )}

        <FormFooterWrapper>
          <FormFooterContent>
            <Button type="button" onClick={() => onCancel()}>
              {t('general.cancel')}
            </Button>
            <Button
              form="send-invoice-form"
              intent="success"
              icon={isSelfPrint ? <FaCheck /> : <FaPaperPlane />}
              disabled={isDisabled}
            >
              {isSelfPrint ? t('invoicing.send.selfPrintSubmit') : t('invoicing.send.sendButton')}
            </Button>
          </FormFooterContent>
        </FormFooterWrapper>
      </FormWrapper>
    </FormProvider>
  )
}

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  form {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    padding-bottom: 1rem;
  }
`

const FormFooterWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const FormFooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
`
